import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';
import { SITE_URL } from '@/lib/appConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const resetSchema = z.object({
  newPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  confirmPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

type ResetFormData = z.infer<typeof resetSchema>;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [hasRecoveryContext, setHasRecoveryContext] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const type = searchParams.get('type') || hashParams.get('type');
    const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
    const code = searchParams.get('code') || hashParams.get('code');
    const error = searchParams.get('error') || hashParams.get('error');
    const errorCode = searchParams.get('error_code') || hashParams.get('error_code');
    const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');
    let active = true;
    const msg = errorDescription?.replace(/\+/g, ' ') || (errorCode === 'otp_expired' ? 'Link de recuperação inválido ou expirado. Solicite um novo link.' : 'Ocorreu um erro ao validar o link de recuperação.');
    if (error) toast.error(msg);

    const init = async () => {
      const shouldShow = type === 'recovery' || !!accessToken || !!code;
      if (shouldShow) setHasRecoveryContext(true);
      if (error) return;
      const { data: sessionData } = await supabase.auth.getSession();
      if (!active) return;
      if (sessionData.session) setHasRecoveryContext(true);
      if (code && !sessionData.session) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (!active) return;
        if (exchangeError) {
          toast.error('Não foi possível validar o link de recuperação. Solicite um novo link.');
        } else {
          setHasRecoveryContext(true);
          const url = new URL(window.location.href);
          url.searchParams.delete('code');
          window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') + url.hash);
        }
      }
    };
    void init();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setHasRecoveryContext(true);
      }
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const form = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const handleReset = async (data: ResetFormData) => {
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: data.newPassword });
    if (error) {
      toast.error(error.message || 'Erro ao atualizar senha');
    } else {
      toast.success('Senha atualizada com sucesso. Faça login novamente.');
      navigate('/auth');
    }
    setLoading(false);
  };

  const handleResendLink = async () => {
    const value = email.trim().toLowerCase();
    if (!value) {
      toast.error('Informe seu email para enviar novo link');
      return;
    }
    setEmailLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(value, { redirectTo: `${SITE_URL}/auth/reset` });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Enviamos um novo link de recuperação para seu email');
    }
    setEmailLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Recuperar Senha</CardTitle>
          <CardDescription>Defina sua nova senha</CardDescription>
        </CardHeader>
        <CardContent>
          {hasRecoveryContext ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleReset)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nova Senha</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Nova senha" {...field} disabled={loading} aria-label="Nova senha" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmar Senha</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Confirme a senha" {...field} disabled={loading} aria-label="Confirme a senha" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 transition-colors" disabled={loading}>
                  {loading ? 'Atualizando...' : 'Atualizar Senha'}
                </Button>
              </form>
            </Form>
          ) : (
            <div className="space-y-4">
              <p>Link inválido ou expirado. Solicite um novo link de recuperação.</p>
              <Input
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={emailLoading}
                aria-label="Email para novo link"
              />
              <Button onClick={handleResendLink} className="w-full" disabled={emailLoading}>
                {emailLoading ? 'Enviando...' : 'Enviar novo link'}
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>Voltar ao login</Button>
        </CardFooter>
      </Card>
    </div>
  );
}
